import {useEffect} from 'react';

import {useSelector} from '../../state/hooks';
import {useViewer} from '../../state/viewer/hooks';
import {getViewerLoading} from '../../state/viewer/selectors';
import {isInternalUser, useIsAdminModeActiveAndIsNotLocalEnv} from '../admin';
import {disableDatadog} from './datadog';
import {setRumUser, startRumSessionReplayRecording} from './datadogRum';

const ANONYMOUS = {id: 'anonymous'} as const;

/**
 * Sets up the Datadog RUM user info when it becomes available.
 * Disables Datadog for SaaS admin mode, not server instance admins.
 *
 * Call once near the app root, inside the Redux provider (it reads viewer
 * state).
 */
export function useDatadogUserSetup(): void {
  const isAdmin = useIsAdminModeActiveAndIsNotLocalEnv();
  const viewerIsLoading = useSelector(getViewerLoading);
  const viewer = useViewer();

  useEffect(() => {
    if (isAdmin) {
      disableDatadog();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (viewerIsLoading) {
      return;
    }
    if (viewer) {
      setRumUser({
        // The non-deprecated setUser requires an id; viewer.username is
        // optional in the Viewer type, so fall back to the viewer id.
        id: viewer.username ?? viewer.id,
        name: viewer.name,
        email: viewer.email,
        accountType: viewer.accountType,
        // A boolean marking W&B-internal employees (@wandb.com or
        // @coreweave.com primary email), so dashboards and monitors can split
        // internal traffic from customer traffic. A flag, not the email
        // itself, stays inside the RUM PII rules and keeps cardinality at 2.
        is_internal: isInternalUser(viewer),
      });
      startRumSessionReplayRecording();
    } else {
      setRumUser(ANONYMOUS);
    }
  }, [viewerIsLoading, viewer]);
}
